import { supabase } from "../lib/supabase";
import type { Database } from "../pages/types/supabase";
import { getIndividualRevenue } from "./revenue";

type Team = Database["public"]["Tables"]["teams"]["Row"];
type TeamInsert = Database["public"]["Tables"]["teams"]["Insert"];
type TeamUpdate = Database["public"]["Tables"]["teams"]["Update"];
type WorkerTeam = Database["public"]["Tables"]["worker_teams"]["Row"];

export interface WorkerWithTeams {
  id: number;
  full_name: string | null;
  email: string | null;
  teams: Team[];
}

export interface TeamWithWorkers extends Team {
  workers: Array<{
    id: number;
    full_name: string | null;
    email: string | null;
  }>;
}

// Teams CRUD operations
export async function getAllTeams(): Promise<Team[]> {
  const { data, error } = await supabase
    .from("teams")
    .select("*")
    .eq("active", true)
    .order("name", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function getTeamById(teamId: number): Promise<Team | null> {
  const { data, error } = await supabase
    .from("teams")
    .select("*")
    .eq("id", teamId)
    .eq("active", true)
    .single();

  if (error) throw error;
  return data;
}

export async function createTeam(team: TeamInsert): Promise<Team> {
  const { data, error } = await supabase
    .from("teams")
    .insert(team)
    .select("*")
    .single();

  if (error) throw error;
  if (!data) throw new Error("Failed to create team");
  return data;
}

export async function updateTeam(id: number, team: TeamUpdate): Promise<Team> {
  const { data, error } = await supabase
    .from("teams")
    .update(team)
    .eq("id", id)
    .select("*")
    .single();

  if (error) throw error;
  if (!data) throw new Error("Team not found");
  return data;
}

export async function deleteTeam(id: number): Promise<void> {
  // Soft delete - set active to false
  const { error } = await supabase
    .from("teams")
    .update({ active: false })
    .eq("id", id);

  if (error) throw error;
}

// Worker-Team relationship operations
export async function getWorkersByTeam(teamId: number): Promise<Array<{ id: number; full_name: string | null; email: string | null; }>> {
  // First, get the worker IDs from worker_teams table
  const { data: workerTeamsData, error: workerTeamsError } = await supabase
    .from("worker_teams")
    .select("worker_id")
    .eq("team_id", teamId)
    .eq("active", true);
  
  if (workerTeamsError) {
    throw workerTeamsError;
  }

  if (!workerTeamsData || workerTeamsData.length === 0) {
    return [];
  }

  const workerIds = workerTeamsData.map(wt => wt.worker_id);

  // Then get the worker details
  const { data: workersData, error: workersError } = await supabase
    .from("workers")
    .select("id, full_name, email")
    .in("id", workerIds);

  if (workersError) {
    throw workersError;
  }

  return workersData || [];
}

export async function getTeamsByWorker(workerId: number): Promise<Team[]> {
  console.log('Fetching teams for worker ID:', workerId);
  
  // First try the foreign key approach
  const { data, error } = await supabase
    .from("worker_teams")
    .select(`
      teams:team_id (
        id,
        name,
        description,
        created_at,
        active
      )
    `)
    .eq("worker_id", workerId)
    .eq("active", true)
    .eq("teams.active", true);

  console.log('Raw worker_teams query result:', data);
  console.log('Query error:', error);

  if (error) {
    console.log('Foreign key query failed, trying alternative approach...');
    
    // Alternative approach: Get team IDs first, then fetch teams separately
    const { data: workerTeamsData, error: workerTeamsError } = await supabase
      .from("worker_teams")
      .select("team_id")
      .eq("worker_id", workerId)
      .eq("active", true);
    
    console.log('Worker teams data:', workerTeamsData);
    
    if (workerTeamsError) throw workerTeamsError;
    
    if (!workerTeamsData || workerTeamsData.length === 0) {
      console.log('No team assignments found for worker');
      return [];
    }
    
    const teamIds = workerTeamsData.map(wt => wt.team_id);
    console.log('Team IDs:', teamIds);
    
    const { data: teamsData, error: teamsError } = await supabase
      .from("teams")
      .select("id, name, description, created_at, active")
      .in("id", teamIds)
      .eq("active", true);
    
    console.log('Teams data:', teamsData);
    
    if (teamsError) throw teamsError;
    return teamsData || [];
  }
  
  const teams = data?.map((item: any) => item.teams).filter(Boolean) || [];
  console.log('Mapped teams:', teams);
  
  return teams;
}

export async function assignWorkerToTeam(
  workerId: number,
  teamId: number,
  assignedByUserId?: string
): Promise<WorkerTeam> {
  const { data: existing, error: existingError } = await supabase
    .from("worker_teams")
    .select("id")
    .eq("worker_id", workerId)
    .eq("team_id", teamId)
    .maybeSingle();

  if (existingError) throw existingError;

  if (existing) {
    const { data, error } = await supabase
      .from("worker_teams")
      .update({
        active: true,
        ...(assignedByUserId ? { assigned_by_user_id: assignedByUserId } : {}),
      })
      .eq("id", existing.id)
      .select("*")
      .single();

    if (error) throw error;
    if (!data) throw new Error("Failed to assign worker to team");
    return data;
  }

  const { data, error } = await supabase
    .from("worker_teams")
    .insert({
      worker_id: workerId,
      team_id: teamId,
      assigned_by_user_id: assignedByUserId || null,
      active: true,
    })
    .select("*")
    .single();

  if (error) throw error;
  if (!data) throw new Error("Failed to assign worker to team");
  return data;
}

export async function removeWorkerFromTeam(
  workerId: number,
  teamId: number
): Promise<void> {
  // Soft delete - set active to false
  const { error } = await supabase
    .from("worker_teams")
    .update({ active: false })
    .eq("worker_id", workerId)
    .eq("team_id", teamId);

  if (error) throw error;
}

export async function updateWorkerTeams(
  workerId: number,
  teamIds: number[],
  assignedByUserId?: string
): Promise<void> {
  // Fetch existing active assignments so we can diff rather than blanket-deactivate
  const { data: currentAssignments, error: currentError } = await supabase
    .from("worker_teams")
    .select("team_id")
    .eq("worker_id", workerId)
    .eq("active", true);

  if (currentError) throw currentError;

  const currentTeamIds = new Set((currentAssignments || []).map((assignment) => assignment.team_id));
  const desiredTeamIds = new Set(teamIds);

  const teamsToAdd = [...desiredTeamIds].filter((teamId) => !currentTeamIds.has(teamId));
  const teamsToDeactivate = [...currentTeamIds].filter((teamId) => !desiredTeamIds.has(teamId));

  if (teamsToAdd.length === 0 && teamsToDeactivate.length === 0) {
    return;
  }

  if (teamsToDeactivate.length > 0) {
    const { error: deactivateError } = await supabase
      .from("worker_teams")
      .update({ active: false })
      .eq("worker_id", workerId)
      .in("team_id", teamsToDeactivate);

    if (deactivateError) throw deactivateError;
  }

  if (teamsToAdd.length > 0) {
    // Check if any of the teams to add already exist in worker_teams (including soft-deleted)
    const { data: existingRecords, error: fetchExistingError } = await supabase
      .from("worker_teams")
      .select("team_id")
      .eq("worker_id", workerId)
      .in("team_id", teamsToAdd);

    if (fetchExistingError) throw fetchExistingError;

    const existingTeamIds = new Set(
      (existingRecords || []).map((record) => record.team_id)
    );
    const teamsToReactivate = teamsToAdd.filter((teamId) =>
      existingTeamIds.has(teamId)
    );
    const trulyNewTeams = teamsToAdd.filter(
      (teamId) => !existingTeamIds.has(teamId)
    );

    if (teamsToReactivate.length > 0) {
      const { error: reactivateError } = await supabase
        .from("worker_teams")
        .update({
          active: true,
          ...(assignedByUserId ? { assigned_by_user_id: assignedByUserId } : {}),
        })
        .eq("worker_id", workerId)
        .in("team_id", teamsToReactivate);

      if (reactivateError) throw reactivateError;
    }

    if (trulyNewTeams.length > 0) {
      const assignments = trulyNewTeams.map((teamId) => ({
        worker_id: workerId,
        team_id: teamId,
        assigned_by_user_id: assignedByUserId || null,
        active: true,
      }));

      const { error: insertError } = await supabase
        .from("worker_teams")
        .insert(assignments);

      if (insertError) throw insertError;
    }
  }
}

// Get all workers with their teams
export async function getWorkersWithTeams(): Promise<WorkerWithTeams[]> {
  const { data, error } = await supabase
    .from("workers")
    .select(`
      id,
      full_name,
      email,
      worker_teams!inner (
        teams:team_id (
          id,
          name,
          description,
          created_at,
          active
        )
      )
    `)
    .eq("worker_teams.active", true)
    .eq("worker_teams.teams.active", true);

  if (error) throw error;

  // Transform the data to group teams by worker
  const workersMap = new Map<number, WorkerWithTeams>();
  
  data?.forEach(worker => {
    if (!workersMap.has(worker.id)) {
      workersMap.set(worker.id, {
        id: worker.id,
        full_name: worker.full_name,
        email: worker.email,
        teams: []
      });
    }
    
    const workerData = workersMap.get(worker.id)!;
    worker.worker_teams.forEach((wt: any) => {
      if (wt.teams) {
        workerData.teams.push(wt.teams);
      }
    });
  });

  return Array.from(workersMap.values());
}

// Get team with all its workers
export async function getTeamWithWorkers(teamId: number): Promise<TeamWithWorkers | null> {
  const team = await getTeamById(teamId);
  if (!team) return null;

  const workers = await getWorkersByTeam(teamId);
  
  return {
    ...team,
    workers
  };
}

// Revenue-related functions for teams
interface RevenueData {
  daily: number;
  weekly: number;
  monthly: number;
  forecast: number;
}

export interface SalesChartData {
  thisWeek: number[];
  lastWeek: number[];
}

export async function getAccumulatedSalesDataForTeam(teamId: number): Promise<SalesChartData> {
  // Get all workers for the team
  const workers = await getWorkersByTeam(teamId);
  const workerIds = workers.map((worker: any) => worker.id);
  
  if (workerIds.length === 0) {
    return { thisWeek: [0, 0, 0, 0, 0, 0, 0], lastWeek: [0, 0, 0, 0, 0, 0, 0] };
  }

  // Get dates for this week and last week
  const today = new Date();
  const thisWeekDates: string[] = [];
  const lastWeekDates: string[] = [];

  // Get Monday of this week
  const monday = new Date(today);
  monday.setDate(monday.getDate() - monday.getDay() + 1);

  // Get dates for this week
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(date.getDate() + i);
    const dateStr = date.toISOString().split("T")[0];
    if (dateStr) thisWeekDates.push(dateStr);
  }

  // Get dates for last week
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(date.getDate() - 7 + i);
    const dateStr = date.toISOString().split("T")[0];
    if (dateStr) lastWeekDates.push(dateStr);
  }

  // Get revenue data for both weeks filtered by team worker IDs
  const { data: revenueData, error: revenueError } = await supabase
    .from("daily_revenue")
    .select("revenue_date, revenue")
    .in("revenue_date", [...thisWeekDates, ...lastWeekDates])
    .in("worker_id", workerIds);

  if (revenueError) {
    throw new Error(`Error fetching team revenue data: ${revenueError.message}`);
  }

  // Group revenue by date
  const revenueLookup: Record<string, number> = {};
  (revenueData || []).forEach((entry) => {
    if (!revenueLookup[entry.revenue_date]) {
      revenueLookup[entry.revenue_date] = 0;
    }
    revenueLookup[entry.revenue_date] += Number(entry.revenue || 0);
  });

  // Fill in the data arrays
  const thisWeek = thisWeekDates.map((date) => revenueLookup[date] || 0);
  const lastWeek = lastWeekDates.map((date) => revenueLookup[date] || 0);

  return {
    thisWeek,
    lastWeek,
  };
}

export async function getTeamRevenue(teamId: number): Promise<RevenueData> {
  // Get all workers for the team
  const workers = await getWorkersByTeam(teamId);
  const workerIds = workers.map((worker: any) => worker.id);
  
  if (workerIds.length === 0) {
    return { daily: 0, weekly: 0, monthly: 0, forecast: 0 };
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 6);
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayStr = formatDate(today);
  const sevenDaysAgoStr = formatDate(sevenDaysAgo);
  const firstDayOfMonthStr = formatDate(firstDayOfMonth);

  const [dailyResult, weeklyResult, monthlyResult] = await Promise.all([
    supabase
      .from("daily_revenue")
      .select("revenue")
      .eq("revenue_date", todayStr)
      .in("worker_id", workerIds),
    supabase
      .from("daily_revenue")
      .select("revenue")
      .gte("revenue_date", sevenDaysAgoStr)
      .lte("revenue_date", todayStr)
      .in("worker_id", workerIds),
    supabase
      .from("daily_revenue")
      .select("revenue")
      .gte("revenue_date", firstDayOfMonthStr)
      .lte("revenue_date", todayStr)
      .in("worker_id", workerIds),
  ]);

  if (dailyResult.error) {
    throw new Error(`Error fetching team daily revenue: ${dailyResult.error.message}`);
  }
  if (weeklyResult.error) {
    throw new Error(`Error fetching team weekly revenue: ${weeklyResult.error.message}`);
  }
  if (monthlyResult.error) {
    throw new Error(`Error fetching team monthly revenue: ${monthlyResult.error.message}`);
  }

  // Sum up all workers' revenue for each period
  const daily = dailyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );
  const weekly = weeklyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );
  const monthly = monthlyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );

  // Calculate team forecast by summing individual forecasts
  let forecast = 0;
  for (const worker of workers) {
    try {
      const workerRevenue = await getIndividualRevenue(worker.id);
      forecast += workerRevenue.forecast;
    } catch (error) {
      // If individual forecast fails, continue with others
      console.warn(`Failed to get forecast for worker ${worker.id}:`, error);
    }
  }

  return {
    daily,
    weekly,
    monthly,
    forecast,
  };
}

export async function getAllTeamsRevenue(): Promise<RevenueData> {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 6);
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayStr = formatDate(today);
  const sevenDaysAgoStr = formatDate(sevenDaysAgo);
  const firstDayOfMonthStr = formatDate(firstDayOfMonth);

  const [dailyResult, weeklyResult, monthlyResult, forecastResult] =
    await Promise.all([
      supabase
        .from("daily_revenue")
        .select("revenue")
        .eq("revenue_date", todayStr)
        .not("worker_id", "is", null),
      supabase
        .from("daily_revenue")
        .select("revenue")
        .gte("revenue_date", sevenDaysAgoStr)
        .lte("revenue_date", todayStr)
        .not("worker_id", "is", null),
      supabase
        .from("daily_revenue")
        .select("revenue")
        .gte("revenue_date", firstDayOfMonthStr)
        .lte("revenue_date", todayStr)
        .not("worker_id", "is", null),
      // Use RPC call to fetch total forecast for the current month
      supabase.rpc("get_current_month_forecast_total"),
    ]);

  if (dailyResult.error) {
    throw new Error(`Error fetching daily revenue: ${dailyResult.error.message}`);
  }
  if (weeklyResult.error) {
    throw new Error(`Error fetching weekly revenue: ${weeklyResult.error.message}`);
  }
  if (monthlyResult.error) {
    throw new Error(`Error fetching monthly revenue: ${monthlyResult.error.message}`);
  }
  if (forecastResult.error) {
    throw new Error(`Error fetching forecast data: ${forecastResult.error.message}`);
  }

  // Sum up all workers' revenue for each period
  const daily = dailyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );
  const weekly = weeklyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );
  const monthly = monthlyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );

  // Use the pre-calculated forecast - comes as a single value from RPC
  const forecast = forecastResult.data?.[0]?.total_forecast || 0;

  return {
    daily,
    weekly,
    monthly,
    forecast,
  };
}

// Multi-team functions for Phase 3 implementation

export async function getWorkerCombinedTeamMembers(workerId: number): Promise<Array<{ id: number; full_name: string | null; email: string | null; }>> {
  // Get all teams for the worker
  const workerTeams = await getTeamsByWorker(workerId);
  
  if (workerTeams.length === 0) {
    return [];
  }

  // Get all unique workers from all teams
  const allTeamMembers = new Map<number, { id: number; full_name: string | null; email: string | null; }>();
  
  for (const team of workerTeams) {
    const teamWorkers = await getWorkersByTeam(team.id);
    teamWorkers.forEach(worker => {
      // Skip the original worker to avoid including them in their own combined list
      if (worker.id !== workerId) {
        allTeamMembers.set(worker.id, worker);
      }
    });
  }

  return Array.from(allTeamMembers.values());
}

export async function getCombinedTeamSalesData(workerId: number): Promise<SalesChartData> {
  // Get all teams for the worker
  const workerTeams = await getTeamsByWorker(workerId);
  
  if (workerTeams.length === 0) {
    return { thisWeek: [0, 0, 0, 0, 0, 0, 0], lastWeek: [0, 0, 0, 0, 0, 0, 0] };
  }

  // Get all unique worker IDs from all teams, including the original worker
  const allWorkerIds = new Set<number>();
  allWorkerIds.add(workerId); // Include the worker themselves
  
  for (const team of workerTeams) {
    const teamWorkers = await getWorkersByTeam(team.id);
    teamWorkers.forEach(worker => {
      allWorkerIds.add(worker.id);
    });
  }

  const workerIdArray = Array.from(allWorkerIds);

  // Get dates for this week and last week
  const today = new Date();
  const thisWeekDates: string[] = [];
  const lastWeekDates: string[] = [];

  // Get Monday of this week
  const monday = new Date(today);
  monday.setDate(monday.getDate() - monday.getDay() + 1);

  // Get dates for this week
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(date.getDate() + i);
    const dateStr = date.toISOString().split("T")[0];
    if (dateStr) thisWeekDates.push(dateStr);
  }

  // Get dates for last week
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(date.getDate() - 7 + i);
    const dateStr = date.toISOString().split("T")[0];
    if (dateStr) lastWeekDates.push(dateStr);
  }

  // Get revenue data for both weeks filtered by all worker IDs
  const { data: revenueData, error: revenueError } = await supabase
    .from("daily_revenue")
    .select("revenue_date, revenue")
    .in("revenue_date", [...thisWeekDates, ...lastWeekDates])
    .in("worker_id", workerIdArray);

  if (revenueError) {
    throw new Error(`Error fetching combined team revenue data: ${revenueError.message}`);
  }

  // Group revenue by date
  const revenueLookup: Record<string, number> = {};
  (revenueData || []).forEach((entry) => {
    if (!revenueLookup[entry.revenue_date]) {
      revenueLookup[entry.revenue_date] = 0;
    }
    revenueLookup[entry.revenue_date] += Number(entry.revenue || 0);
  });

  // Fill in the data arrays
  const thisWeek = thisWeekDates.map((date) => revenueLookup[date] || 0);
  const lastWeek = lastWeekDates.map((date) => revenueLookup[date] || 0);

  return {
    thisWeek,
    lastWeek,
  };
}

export async function getMultiTeamRevenue(workerId: number): Promise<RevenueData> {
  // Get all teams for the worker
  const workerTeams = await getTeamsByWorker(workerId);
  
  if (workerTeams.length === 0) {
    return { daily: 0, weekly: 0, monthly: 0, forecast: 0 };
  }

  // Get all unique worker IDs from all teams, including the original worker
  const allWorkerIds = new Set<number>();
  allWorkerIds.add(workerId); // Include the worker themselves
  
  for (const team of workerTeams) {
    const teamWorkers = await getWorkersByTeam(team.id);
    teamWorkers.forEach(worker => {
      allWorkerIds.add(worker.id);
    });
  }

  const workerIdArray = Array.from(allWorkerIds);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 6);
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayStr = formatDate(today);
  const sevenDaysAgoStr = formatDate(sevenDaysAgo);
  const firstDayOfMonthStr = formatDate(firstDayOfMonth);

  const [dailyResult, weeklyResult, monthlyResult] = await Promise.all([
    supabase
      .from("daily_revenue")
      .select("revenue")
      .eq("revenue_date", todayStr)
      .in("worker_id", workerIdArray),
    supabase
      .from("daily_revenue")
      .select("revenue")
      .gte("revenue_date", sevenDaysAgoStr)
      .lte("revenue_date", todayStr)
      .in("worker_id", workerIdArray),
    supabase
      .from("daily_revenue")
      .select("revenue")
      .gte("revenue_date", firstDayOfMonthStr)
      .lte("revenue_date", todayStr)
      .in("worker_id", workerIdArray),
  ]);

  if (dailyResult.error) {
    throw new Error(`Error fetching multi-team daily revenue: ${dailyResult.error.message}`);
  }
  if (weeklyResult.error) {
    throw new Error(`Error fetching multi-team weekly revenue: ${weeklyResult.error.message}`);
  }
  if (monthlyResult.error) {
    throw new Error(`Error fetching multi-team monthly revenue: ${monthlyResult.error.message}`);
  }

  // Sum up all workers' revenue for each period
  const daily = dailyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );
  const weekly = weeklyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );
  const monthly = monthlyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );

  // Calculate combined forecast by summing individual forecasts
  let forecast = 0;
  for (const workerId of workerIdArray) {
    try {
      const workerRevenue = await getIndividualRevenue(workerId);
      forecast += workerRevenue.forecast;
    } catch (error) {
      console.warn(`Failed to get forecast for worker ${workerId}:`, error);
    }
  }

  return {
    daily,
    weekly,
    monthly,
    forecast,
  };
}

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

interface LeaderboardEntry {
  worker_id: number;
  name: string;
  contract_count: number;
  revenue?: number;
}

export async function getMultiTeamLeaderboardData(workerId: number, dateRange: DateRange, mode: 'contracts' | 'revenue' = 'contracts'): Promise<LeaderboardEntry[]> {
  // Get all teams for the worker
  const workerTeams = await getTeamsByWorker(workerId);
  
  if (workerTeams.length === 0) {
    return [];
  }

  // Get all unique worker IDs from all teams, including the original worker
  const allWorkerIds = new Set<number>();
  allWorkerIds.add(workerId); // Include the worker themselves
  
  for (const team of workerTeams) {
    const teamWorkers = await getWorkersByTeam(team.id);
    teamWorkers.forEach(worker => {
      allWorkerIds.add(worker.id);
    });
  }

  const workerIdArray = Array.from(allWorkerIds);

  // Prepare date range
  const startDate = dateRange.startDate || new Date();
  const endDate = dateRange.endDate || new Date();
  
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  if (mode === 'contracts') {
    const startDateStr = startDate.toISOString();
    const endDateStr = endDate.toISOString();

    // Fetch contract data for all workers in the teams
    const { data: contractData, error: contractError } = await supabase
      .from("contracts")
      .select(`
        worker_id,
        workers!inner (
          full_name
        )
      `)
      .gte("created_at", startDateStr)
      .lt("created_at", endDateStr)
      .in("worker_id", workerIdArray);

    if (contractError) {
      throw new Error(`Error fetching multi-team leaderboard data: ${contractError.message}`);
    }

    // Process contract counts
    const workerCounts: Record<number, { name: string; count: number }> = {};
    
    (contractData || []).forEach((contract: any) => {
      const workerId = contract.worker_id;
      const workerName = contract.workers.full_name || "Unknown Worker";

      if (!workerCounts[workerId]) {
        workerCounts[workerId] = { name: workerName, count: 0 };
      }
      workerCounts[workerId]!.count += 1;
    });

    // Convert to leaderboard format and sort
    const leaderboardData = Object.entries(workerCounts)
      .map(([workerId, stats]) => ({
        worker_id: parseInt(workerId),
        name: stats.name,
        contract_count: stats.count,
      }))
      .sort((a, b) => b.contract_count - a.contract_count);

    return leaderboardData;
  } else {
    // Revenue mode
    const formatDateToGermanDateString = (date: Date): string => {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone: "Europe/Berlin",
      });
      return formatter.format(date);
    };

    const startDateFormatted = formatDateToGermanDateString(startDate);
    const endDateFormatted = formatDateToGermanDateString(endDate);

    // Determine if it's a single day or date range
    const isSingleDay = startDate.getTime() === endDate.getTime();

    let query = supabase.from("daily_revenue").select(
      `
        worker_id,
        revenue,
        workers (
          full_name
        )
      `
    );

    if (isSingleDay) {
      query = query.eq("revenue_date", startDateFormatted);
    } else {
      query = query
        .gte("revenue_date", startDateFormatted)
        .lte("revenue_date", endDateFormatted);
    }

    // Filter by team worker IDs
    query = query.in("worker_id", workerIdArray);

    const { data: revenueData, error: revenueError } = await query;

    if (revenueError) {
      throw new Error(`Error fetching multi-team revenue data: ${revenueError.message}`);
    }

    // Process revenue data
    const workerRevenue: Record<number, { name: string; revenue: number }> = {};
    
    (revenueData || []).forEach((entry: any) => {
      if (entry && typeof entry.worker_id === "number") {
        const workerId = entry.worker_id;
        let workerName = "Unknown Worker";
        if (entry.workers) {
          if (Array.isArray(entry.workers)) {
            workerName = entry.workers[0]?.full_name || "Unknown Worker";
          } else if (typeof entry.workers === "object" && entry.workers.full_name) {
            workerName = entry.workers.full_name;
          }
        }
        const revenue = Number(entry.revenue) || 0;

        if (typeof workerRevenue[workerId] === "undefined") {
          workerRevenue[workerId] = { name: workerName, revenue: 0 };
        }
        workerRevenue[workerId]!.revenue += revenue;
      }
    });

    // Convert to leaderboard format and sort
    const leaderboardData = Object.entries(workerRevenue)
      .map(([workerId, stats]) => ({
        worker_id: parseInt(workerId),
        name: stats.name,
        contract_count: 0,
        revenue: stats.revenue,
      }))
      .filter((worker) => worker.revenue > 0)
      .sort((a, b) => (b.revenue || 0) - (a.revenue || 0));

    return leaderboardData;
  }
}

export async function getWorkerTeamContext(workerId: number): Promise<{
  teamCount: number;
  teams: Team[];
  hasMultipleTeams: boolean;
  hasNoTeams: boolean;
}> {
  console.log('Getting worker team context for worker ID:', workerId);
  
  const teams = await getTeamsByWorker(workerId);
  
  const context = {
    teamCount: teams.length,
    teams,
    hasMultipleTeams: teams.length > 1,
    hasNoTeams: teams.length === 0,
  };
  
  console.log('Worker team context:', context);
  
  return context;
}
